/**
 * ManualSectionsPanel — user-authored "temp" sections on the Brief tab.
 *
 * Sits above the auto-detected section list. Lets a PM add a section
 * the parser missed (e.g. a hand-stamped clause on a corrigendum) and
 * edit / delete their own entries. These rows live in the
 * ``rfp_manual_section`` table (Priority #4) and survive a re-analyse,
 * unlike anything inside rfp.analysis['sections'].
 *
 * We deliberately don't run the SectionReviewBar machinery on these —
 * you don't need to "accept" or "re-analyse" your own typed content;
 * that machinery exists to grade the parser.
 *
 * Props:
 *   rfpId       string   — required.
 *   departments array    — optional; [{id, name}] list from workspace.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Plus, Pencil, Trash2, Loader2, AlertTriangle, ClipboardEdit,
} from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";


const EMPTY_FORM = {
  title: "",
  body: "",
  page_start: "",
  page_end: "",
  dept_id: "",
};


function formToPayload(form) {
  const trim = (s) => (typeof s === "string" ? s.trim() : s);
  const numOrNull = (s) => {
    if (s === "" || s == null) return null;
    const n = Number(s);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
  };
  return {
    title: trim(form.title),
    body: trim(form.body) || null,
    page_start: numOrNull(form.page_start),
    page_end: numOrNull(form.page_end),
    dept_id: form.dept_id || null,
  };
}


export default function ManualSectionsPanel({ rfpId, departments = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [composerOpen, setComposerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!rfpId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await v2Api.rfp.listManualSections(rfpId);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Could not load manual sections");
    } finally {
      setLoading(false);
    }
  }, [rfpId]);

  useEffect(() => { reload(); }, [reload]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      title: row.title || "",
      body: row.body || "",
      page_start: row.page_start ?? "",
      page_end: row.page_end ?? "",
      dept_id: row.dept_id ?? "",
    });
    setComposerOpen(true);
  };

  const cancel = () => {
    setComposerOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    const payload = formToPayload(form);
    if (!payload.title) {
      toast.error("Title is required");
      return;
    }
    if (payload.page_start && payload.page_end && payload.page_end < payload.page_start) {
      toast.error("page_end must be ≥ page_start");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        const updated = await v2Api.rfp.patchManualSection(editingId, payload);
        setRows((prev) => prev.map((r) => r.id === editingId ? updated : r));
        toast.success("Manual section updated");
      } else {
        const created = await v2Api.rfp.createManualSection(rfpId, payload);
        setRows((prev) => [...prev, created]);
        toast.success("Manual section added");
      }
      cancel();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ?? err?.message ?? "Could not save manual section"
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!await confirm(`Delete "${row.title}"?`, { title: "Delete section", confirmLabel: "Delete" })) return;
    try {
      await v2Api.rfp.deleteManualSection(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Manual section deleted");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ?? err?.message ?? "Could not delete manual section"
      );
    }
  };

  const deptName = (id) => departments.find((d) => d.id === id)?.name;

  return (
    <Card className="border-[#E2E8F0]">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardEdit className="w-4 h-4 text-[#64748B]" />
            <h3 className="text-sm font-semibold text-slate-800">
              Manual sections
            </h3>
            <Badge variant="outline" className="text-[10px] text-slate-500">
              {rows.length}
            </Badge>
          </div>
          {!composerOpen && (
            <Button
              size="sm" variant="outline"
              onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setComposerOpen(true); }}
              className="h-7 text-xs gap-1"
            >
              <Plus className="w-3 h-3" /> Add section
            </Button>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-rose-700">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {!loading && !error && rows.length === 0 && !composerOpen && (
          <p className="text-xs italic text-slate-400">
            No manual sections yet. Use "Add section" to capture clauses
            the parser missed — they'll stay even after re-analyse.
          </p>
        )}

        {composerOpen && (
          <div className="border rounded-md p-3 bg-slate-50 space-y-2">
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Section title (required)"
              maxLength={400}
            />
            <Textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Content (paste or type the clause text)…"
              rows={4}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={form.page_start}
                onChange={(e) => setForm((f) => ({ ...f, page_start: e.target.value }))}
                placeholder="Page start (optional)"
                type="number" min="1"
              />
              <Input
                value={form.page_end}
                onChange={(e) => setForm((f) => ({ ...f, page_end: e.target.value }))}
                placeholder="Page end (optional)"
                type="number" min="1"
              />
            </div>
            {departments.length > 0 && (
              <select
                value={form.dept_id}
                onChange={(e) => setForm((f) => ({ ...f, dept_id: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">— No department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            )}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={cancel} disabled={busy}>
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={busy || !form.title.trim()}>
                {busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                {editingId ? "Save changes" : "Add section"}
              </Button>
            </div>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li key={row.id}
                  className="border rounded-md p-3 bg-white">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                        Manual
                      </Badge>
                      {row.dept_id && deptName(row.dept_id) && (
                        <Badge variant="outline" className="text-[10px] text-slate-600">
                          {deptName(row.dept_id)}
                        </Badge>
                      )}
                      <h4 className="text-sm font-semibold text-slate-900 truncate">
                        {row.title}
                      </h4>
                      {row.page_start != null && (
                        <Badge variant="outline" className="text-[10px]">
                          p.{row.page_start}
                          {row.page_end && row.page_end !== row.page_start
                            ? `–${row.page_end}`
                            : ""}
                        </Badge>
                      )}
                    </div>
                    {row.body && (
                      <pre className="mt-2 text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                        {row.body}
                      </pre>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => startEdit(row)}
                      className="h-7 px-2 text-xs gap-1"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => remove(row)}
                      className="h-7 px-2 text-xs gap-1 text-rose-600 hover:text-rose-700"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
